import {
    Container, Box, Grid, Tabs, Tab,
    useMediaQuery, IconButton, Dialog
} from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import PropTypes from 'prop-types';

const LazyImage = ({ src, onClick }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = useRef();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.unobserve(entry.target);
                }
            },
            { rootMargin: '200px', threshold: 0.01 }
        );

        if (imgRef.current) observer.observe(imgRef.current);
        return () => { if (imgRef.current) observer.unobserve(imgRef.current); };
    }, []);

    return (
        <Box
            ref={imgRef}
            sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={onClick}
        >
            {isInView && (
                <Box
                    component="img"
                    src={src}
                    loading="lazy"
                    onLoad={() => setIsLoaded(true)}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: isLoaded ? 1 : 0,
                        transition: 'opacity 0.5s ease'
                    }}
                />
            )}
        </Box>
    );
};

const GallerySection = ({ language = 'EN' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0);
    const [lightroomOpen, setLightroomOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);

    // Language content
    const content = {
        EN: {
            categories: [
                { name: "Workshops" },
                { name: "Corporate Visit" },
                { name: "Charity" },
                { name: "Institution" },
                { name: "News & Media" }
            ],
            imageCounter: (current, total) => `${current} / ${total}`,
            zoomIndicator: (level) => `${Math.round(level * 100)}%`,
            controls: {
                close: "Close",
                fullscreen: "Fullscreen",
                exitFullscreen: "Exit Fullscreen",
                zoomIn: "Zoom In",
                zoomOut: "Zoom Out",
                prev: "Previous",
                next: "Next"
            }
        },
        BM: {
            categories: [
                { name: "Bengkel" },
                { name: "Lawatan Korporat" },
                { name: "Amal" },
                { name: "Institusi" },
                { name: "Berita & Media" }
            ],
            imageCounter: (current, total) => `${current} / ${total}`,
            zoomIndicator: (level) => `${Math.round(level * 100)}%`,
            controls: {
                close: "Tutup",
                fullscreen: "Skrin Penuh",
                exitFullscreen: "Keluar Skrin Penuh",
                zoomIn: "Zum Masuk",
                zoomOut: "Zum Keluar",
                prev: "Sebelum",
                next: "Seterus"
            }
        },
        CN: {
            categories: [
                { name: "工作坊" },
                { name: "企业访问" },
                { name: "慈善活动" },
                { name: "机构合作" },
                { name: "新闻媒体" }
            ],
            imageCounter: (current, total) => `${current} / ${total}`,
            zoomIndicator: (level) => `${Math.round(level * 100)}%`,
            controls: {
                close: "关闭",
                fullscreen: "全屏",
                exitFullscreen: "退出全屏",
                zoomIn: "放大",
                zoomOut: "缩小",
                prev: "上一张",
                next: "下一张"
            }
        }
    };

    const currentContent = content[language] || content.EN;

    // Organized image collections (same across languages)
    const galleryCategories = [
        {
            name: currentContent.categories[0].name,
            images: [
                "/images/gallery/workshop/workshop1.jpg",
                "/images/gallery/workshop/workshop2.jpg",
                "/images/gallery/workshop/workshop3.jpg",
                "/images/gallery/workshop/workshop4.jpg",
                "/images/gallery/workshop/workshop5.jpg",
                "/images/gallery/workshop/workshop6.jpg",
                "/images/gallery/workshop/workshop7.jpg",
            ]
        },
        {
            name: currentContent.categories[1].name,
            images: [
                "/images/gallery/corporateVisit/corp1.jpg",
                "/images/gallery/corporateVisit/corp2.jpg",
                "/images/gallery/corporateVisit/corp3.jpg",
                "/images/gallery/corporateVisit/corp4.jpg",
                "/images/gallery/corporateVisit/corp5.jpg",
                "/images/gallery/corporateVisit/corp6.jpg",
                "/images/gallery/corporateVisit/corp7.jpg",
                "/images/gallery/corporateVisit/corp8.jpg",
            ]
        },
        {
            name: currentContent.categories[2].name,
            images: [
                "/images/gallery/charity/cha1.jpg",
                "/images/gallery/charity/cha2.jpg",
                "/images/gallery/charity/cha3.jpg",
                "/images/gallery/charity/cha4.jpg",
                "/images/gallery/charity/cha5.jpg",
                "/images/gallery/charity/cha6.jpg",
                "/images/gallery/charity/cha7.jpg",
            ]
        },
        {
            name: currentContent.categories[3].name,
            images: [
                "/images/gallery/institute/institute1.jpg",
                "/images/gallery/institute/institute2.jpg",
                "/images/gallery/institute/institute3.jpg",
                "/images/gallery/institute/institute4.jpg",
                "/images/gallery/institute/institute5.jpg",
                "/images/gallery/institute/institute6.jpg",
                "/images/gallery/institute/institute7.jpg",
                "/images/gallery/institute/institute8.jpg",
                "/images/gallery/institute/institute9.jpg",
                "/images/gallery/institute/institute10.jpg",
            ]
        },
        {
            name: currentContent.categories[4].name,
            images: [
                "/images/gallery/news/news1.jpg",
                "/images/gallery/news/news2.jpg",
                "/images/gallery/news/news3.jpg",
                "/images/gallery/news/news4.jpg",
            ]
        },
    ];

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setLightroomOpen(false);
    };

    const openLightroom = (index) => {
        setCurrentImageIndex(index);
        setLightroomOpen(true);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    const closeLightroom = () => {
        setLightroomOpen(false);
        setIsFullscreen(false);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

    const navigateImages = (direction) => {
        const totalImages = galleryCategories[activeTab].images.length;
        setCurrentImageIndex(prevIndex => {
            const newIndex = direction === 'prev'
                ? (prevIndex - 1 + totalImages) % totalImages
                : (prevIndex + 1) % totalImages;
            return newIndex;
        });
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.5, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.5, 1));
    };

    const handleWheel = (e) => {
        if (e.ctrlKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoomLevel(prev => Math.min(Math.max(prev + delta, 1), 3));
        } else {
            setPosition(prev => ({
                x: prev.x - e.deltaX,
                y: prev.y - e.deltaY
            }));
        }
    };

    const handleMouseDown = (e) => {
        if (zoomLevel > 1) {
            const startX = e.clientX - position.x;
            const startY = e.clientY - position.y;

            const handleMouseMove = (e) => {
                setPosition({
                    x: e.clientX - startX,
                    y: e.clientY - startY
                });
            };

            const handleMouseUp = () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
    };

    // Reset zoom and position when image changes
    useEffect(() => {
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    }, [currentImageIndex]);

    return (
        <Box sx={{
            py: 8,
            backgroundColor: theme.palette.background.default,
            position: 'relative'
        }}>
            <Container maxWidth="lg">
                {/* Category Tabs */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 4,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: theme.palette.background.default,
                    py: 2,
                    boxShadow: `0 2px 5px ${theme.palette.divider}`
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme.palette.primary.main,
                                height: 3,
                                borderRadius: '3px'
                            }
                        }}
                    >
                        {galleryCategories.map((category, index) => (
                            <Tab
                                key={index}
                                label={category.name}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    minWidth: 'unset',
                                    px: 3,
                                    mx: 1,
                                    fontWeight: activeTab === index ? 'bold' : 'normal',
                                    color: activeTab === index ? theme.palette.primary.main : theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary.dark
                                    }
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* Gallery Grid - Equal Sized Images */}
                <Grid container spacing={3}>
                    {galleryCategories[activeTab].images.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                                component={motion.div}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                whileHover={{ scale: 1.02 }}
                                sx={{
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    boxShadow: theme.shadows[3],
                                    height: 300, // Fixed height for all images
                                    '&:hover': {
                                        boxShadow: theme.shadows[6],
                                        '&:after': {
                                            opacity: 1
                                        }
                                    },
                                    '&:after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'rgba(0,0,0,0.2)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease'
                                    }
                                }}
                                onClick={() => openLightroom(index)}
                            >
                                <LazyImage src={image} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Lightroom Viewer */}
                <Dialog
                    open={lightroomOpen}
                    onClose={closeLightroom}
                    maxWidth={isFullscreen ? false : 'lg'}
                    fullScreen={isFullscreen}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgba(0,0,0,0.9)',
                            boxShadow: 'none',
                            margin: 0,
                            maxWidth: 'none',
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: zoomLevel > 1 ? 'grab' : 'default',
                            ':active': {
                                cursor: zoomLevel > 1 ? 'grabbing' : 'default'
                            }
                        }}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                    >
                        {/* Navigation Arrows */}
                        <IconButton
                            onClick={() => navigateImages('prev')}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                },
                                [theme.breakpoints.down('sm')]: {
                                    left: 8
                                }
                            }}
                            aria-label={currentContent.controls.prev}
                        >
                            <NavigateBeforeIcon fontSize="large" />
                        </IconButton>

                        <IconButton
                            onClick={() => navigateImages('next')}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                },
                                [theme.breakpoints.down('sm')]: {
                                    right: 8
                                }
                            }}
                            aria-label={currentContent.controls.next}
                        >
                            <NavigateNextIcon fontSize="large" />
                        </IconButton>

                        {/* Close Button */}
                        <IconButton
                            onClick={closeLightroom}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                }
                            }}
                            aria-label={currentContent.controls.close}
                        >
                            <CloseIcon fontSize="large" />
                        </IconButton>

                        {/* Fullscreen Toggle */}
                        <IconButton
                            onClick={toggleFullscreen}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 80,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                }
                            }}
                            aria-label={isFullscreen ? currentContent.controls.exitFullscreen : currentContent.controls.fullscreen}
                        >
                            {isFullscreen ? (
                                <FullscreenExitIcon fontSize="large" />
                            ) : (
                                <FullscreenIcon fontSize="large" />
                            )}
                        </IconButton>

                        {/* Zoom Controls */}
                        <Box sx={{
                            position: 'absolute',
                            right: 16,
                            top: 144,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            zIndex: 1
                        }}>
                            <IconButton
                                onClick={handleZoomIn}
                                disabled={zoomLevel >= 3}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)'
                                    },
                                    '&:disabled': {
                                        opacity: 0.5
                                    }
                                }}
                                aria-label={currentContent.controls.zoomIn}
                            >
                                <ZoomInIcon fontSize="large" />
                            </IconButton>
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={zoomLevel <= 1}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)'
                                    },
                                    '&:disabled': {
                                        opacity: 0.5
                                    }
                                }}
                                aria-label={currentContent.controls.zoomOut}
                            >
                                <ZoomOutIcon fontSize="large" />
                            </IconButton>
                        </Box>

                        {/* Current Image */}
                        <Box
                            component={motion.div}
                            key={currentImageIndex}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            style={{
                                transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                                transformOrigin: 'center center'
                            }}
                            sx={{
                                maxWidth: '90%',
                                maxHeight: '90%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                touchAction: 'none'
                            }}
                        >
                            <Box
                                ref={imageRef}
                                component="img"
                                src={galleryCategories[activeTab].images[currentImageIndex]}
                                loading="eager"
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    userSelect: 'none'
                                }}
                                draggable="false"
                            />
                        </Box>

                        {/* Image Counter */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            px: 2,
                            py: 1,
                            borderRadius: '4px'
                        }}>
                            {currentContent.imageCounter(currentImageIndex + 1, galleryCategories[activeTab].images.length)}
                        </Box>

                        {/* Zoom Level Indicator */}
                        {zoomLevel > 1 && (
                            <Box sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                px: 2,
                                py: 1,
                                borderRadius: '4px'
                            }}>
                                {currentContent.zoomIndicator(zoomLevel)}
                            </Box>
                        )}
                    </Box>
                </Dialog>
            </Container>
        </Box>
    );
};

GallerySection.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN'])
};

export default GallerySection;